import { Building2Icon, CheckIcon, ChevronDownIcon, Loader2Icon, UsersRoundIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  mutateThreadCollaborator,
  useThreadCollaboration,
  type CollaborationMember,
} from "~/state/collaboration";
import { cn } from "~/lib/utils";
import { useActiveOrg } from "~/state/orgs";
import { mutateThreadSettings, useThreadSettings } from "~/state/threadSettings";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import { Switch } from "../ui/switch";
import { toastManager } from "../ui/toast";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";

function memberLabel(member: CollaborationMember): string {
  const name = [member.firstName, member.lastName].filter(Boolean).join(" ").trim();
  return name.length > 0 ? name : member.email;
}

function memberInitial(member: CollaborationMember): string {
  return (member.firstName ?? member.email).slice(0, 1).toUpperCase();
}

function MemberAvatar({ member, className }: { member: CollaborationMember; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground",
        className,
      )}
    >
      {memberInitial(member)}
    </span>
  );
}

/**
 * Collaborate control: sits where the git actions used to, top-right of the
 * chat header. The owner (or the server operator) shares a thread with other
 * members of its org through the same search-and-pick affordance used across
 * the app; everyone else sees a read-only "Shared" label. Both people watching
 * a shared thread see the same live view — that's fine, so a share is just a
 * grant, nothing per-viewer.
 */
export function CollaborateControl({ threadId }: { readonly threadId: string }) {
  const collaboration = useThreadCollaboration(threadId);
  const settings = useThreadSettings(threadId);
  const orgName = useActiveOrg()?.name ?? "your organization";
  const everyone = settings.visibility === "public";
  // The switch's requested position, held until the settings re-fetch agrees
  // or fails, so a failed re-fetch never leaves the switch stuck.
  const [requestedEveryone, setRequestedEveryone] = useState<boolean | null>(null);
  const settingsFailed = settings.load._tag === "failed";
  useEffect(() => {
    if (requestedEveryone !== null && (requestedEveryone === everyone || settingsFailed)) {
      setRequestedEveryone(null);
    }
  }, [everyone, requestedEveryone, settingsFailed]);
  const setEveryone = async (checked: boolean) => {
    setRequestedEveryone(checked);
    const saved = await mutateThreadSettings(threadId, {
      visibility: checked ? "public" : "private",
    }).catch(() => false);
    if (!saved) {
      setRequestedEveryone(null);
      toastManager.add({ type: "error", title: "Couldn’t change who can see this thread" });
    }
  };
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);

  const normalizedQuery = query.trim().toLowerCase();
  const filteredAddable = useMemo(
    () =>
      normalizedQuery.length === 0
        ? collaboration.addableMembers
        : collaboration.addableMembers.filter((member) =>
            `${memberLabel(member)} ${member.email}`.toLowerCase().includes(normalizedQuery),
          ),
    [collaboration.addableMembers, normalizedQuery],
  );

  // Nothing to show until the thread resolves to a tenant (feature configured).
  if (!collaboration.canManage && !collaboration.isSharedWithViewer && collaboration.owner === null) {
    return null;
  }

  const collaboratorCount = collaboration.collaborators.length;
  const triggerLabel = collaboration.isSharedWithViewer ? "Shared" : "Collaborate";

  const toggle = async (userId: string, action: "add" | "remove") => {
    setPendingUserId(userId);
    try {
      await mutateThreadCollaborator(threadId, userId, action);
    } finally {
      setPendingUserId(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={triggerLabel}
            className="gap-1.5"
          />
        }
      >
        <Tooltip>
          <TooltipTrigger render={<span className="flex items-center gap-[inherit]" />}>
            <UsersRoundIcon className="size-4" />
            <span className="max-sm:hidden">{triggerLabel}</span>
            {collaboratorCount > 0 ? (
              <span className="rounded-full bg-muted px-1.5 text-xs font-medium text-muted-foreground">
                {collaboratorCount}
              </span>
            ) : null}
            <ChevronDownIcon className="size-3.5 text-muted-foreground" />
          </TooltipTrigger>
          <TooltipPopup side="bottom">
            {collaboration.isSharedWithViewer
              ? "Shared with you by the thread owner"
              : "Share this thread with your team"}
          </TooltipPopup>
        </Tooltip>
      </PopoverTrigger>

      <PopoverPopup align="end" className="w-80 p-0">
        <div className="flex flex-col gap-1 border-b border-border/60 px-3 py-2.5">
          <div className="text-sm font-medium text-foreground">People with access</div>
          <div className="text-xs text-muted-foreground">
            {collaboration.canManage
              ? "Share this thread with members of your organization."
              : "You have access to this shared thread."}
          </div>
        </div>

        <div className="max-h-56 overflow-y-auto px-1.5 py-1.5">
          {collaboration.owner ? (
            <div className="flex items-center gap-2 rounded-md px-2 py-1.5">
              <MemberAvatar member={collaboration.owner} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-foreground">
                  {memberLabel(collaboration.owner)}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {collaboration.owner.email}
                </div>
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">Owner</span>
            </div>
          ) : null}

          {collaboration.collaborators.map((member) => (
            <div key={member.userId} className="flex items-center gap-2 rounded-md px-2 py-1.5">
              <MemberAvatar member={member} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-foreground">
                  {memberLabel(member)}
                </div>
                <div className="truncate text-xs text-muted-foreground">{member.email}</div>
              </div>
              {collaboration.canManage ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 shrink-0 px-2 text-xs text-muted-foreground hover:text-foreground"
                  disabled={pendingUserId === member.userId}
                  onClick={() => toggle(member.userId, "remove")}
                >
                  {pendingUserId === member.userId ? (
                    <Loader2Icon className="size-3.5 animate-spin" />
                  ) : (
                    "Remove"
                  )}
                </Button>
              ) : (
                <span className="shrink-0 text-xs text-muted-foreground">Shared</span>
              )}
            </div>
          ))}
        </div>

        {settings.available ? (
          <div className="flex items-center gap-2 border-t border-border/60 px-3.5 py-2.5">
            <span
              aria-hidden="true"
              className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"
            >
              <Building2Icon className="size-3.5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-foreground">Everyone in {orgName}</div>
              <div className="truncate text-xs text-muted-foreground">
                {(requestedEveryone ?? everyone) ? "Can view this thread" : "Only the people above"}
              </div>
            </div>
            <Switch
              checked={requestedEveryone ?? everyone}
              disabled={!settings.canSetVisibility || requestedEveryone !== null}
              aria-label={`Share with everyone in ${orgName}`}
              onCheckedChange={(checked) => void setEveryone(checked)}
            />
          </div>
        ) : null}

        {collaboration.canManage ? (
          <div className="border-t border-border/60 p-2">
            <Input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              placeholder="Add a team member…"
              aria-label="Search team members"
              className="mb-1.5"
            />
            <div className="max-h-40 overflow-y-auto">
              {filteredAddable.length === 0 ? (
                <p className="px-2 py-3 text-center text-xs text-muted-foreground">
                  {collaboration.addableMembers.length === 0
                    ? "Everyone is already added."
                    : "No members found."}
                </p>
              ) : (
                filteredAddable.map((member) => (
                  <button
                    key={member.userId}
                    type="button"
                    disabled={pendingUserId === member.userId}
                    onClick={() => toggle(member.userId, "add")}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-hidden disabled:opacity-64"
                  >
                    <MemberAvatar member={member} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-foreground">
                        {memberLabel(member)}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">{member.email}</div>
                    </div>
                    {pendingUserId === member.userId ? (
                      <Loader2Icon className="size-3.5 shrink-0 animate-spin text-muted-foreground" />
                    ) : (
                      <CheckIcon className="size-3.5 shrink-0 text-transparent" />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        ) : null}
      </PopoverPopup>
    </Popover>
  );
}
